import {
  Firewall,
  Operation,
  TERMINAL_RULE_DESCRIPTION,
  TerminalHostList,
  TerminalReachability,
  TerminalSetup,
  type FirewallRuleInput,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client.js";
import { keys } from "./queries.js";

/*
 * The browser terminal (docs/terminal.md): what a server needs to trust our
 * SSH CA, and whether its firewalls let the panel reach port 22. Sessions
 * themselves are opened by `terminal/connections.ts`.
 */

const base = (projectId: string, serverId: string) =>
  `/projects/${projectId}/servers/${serverId}/terminal`;

export const useTerminalSetup = (projectId: string, serverId: string, enabled = true) =>
  useQuery({
    queryKey: ["projects", projectId, "servers", serverId, "terminal", "setup"],
    queryFn: () => api(`${base(projectId, serverId)}/setup`, { schema: TerminalSetup }),
    enabled,
  });

/** The project's servers where a certificate sign-in worked (the picker connects to them at once). */
export const useTerminalHosts = (projectId: string, enabled = true) =>
  useQuery({
    queryKey: ["projects", projectId, "terminal", "hosts"],
    queryFn: () => api(`/projects/${projectId}/terminal/hosts`, { schema: TerminalHostList }),
    enabled: enabled && projectId !== "",
    // A sign-in from another tab or a moment ago counts: ask each time the picker opens.
    staleTime: 0,
  });

export const useTerminalReachability = (projectId: string, serverId: string, enabled = true) =>
  useQuery({
    queryKey: ["projects", projectId, "servers", serverId, "terminal", "reachability"],
    queryFn: () =>
      api(`${base(projectId, serverId)}/reachability`, { schema: TerminalReachability }),
    enabled,
  });

/**
 * One inbound rule: TCP 22 (or the remote desktop's 3389, with its own
 * description) from our outgoing addresses (as /32 and /128).
 */
export function terminalRule(
  egressIps: readonly string[],
  port = 22,
  description = TERMINAL_RULE_DESCRIPTION,
): FirewallRuleInput {
  return {
    direction: "in",
    protocol: "tcp",
    port: String(port),
    sourceIps: egressIps.map((ip) => (ip.includes(":") ? `${ip}/128` : `${ip}/32`)),
    description,
  };
}

/**
 * "Allow access from the panel's address": adds the terminal rule to a
 * firewall through the normal rules route (the whole list is replaced, so
 * the current rules are read first).
 */
export function useAllowTerminal(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      firewallId,
      egressIps,
      port,
      description,
    }: {
      firewallId: string;
      egressIps: string[];
      port?: number;
      description?: string;
    }) => {
      const firewall = await api(`/projects/${projectId}/firewalls/${firewallId}`, {
        schema: Firewall,
      });
      return api(`/projects/${projectId}/firewalls/${firewallId}/rules`, {
        method: "PUT",
        body: { rules: [...firewall.rules, terminalRule(egressIps, port, description)] },
        schema: Operation,
      });
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.projects }),
  });
}
